import { z } from "zod";
import { chatMessageSchema } from "./chat";
import { handoffDtoSchema, handoffToRoleSchema } from "./handoff";
import {
  MEDICAL_QUESTION_CATEGORIES,
  MEDICAL_QUESTION_CATEGORY_LABELS,
  medicalQuestionCategorySchema,
  type MedicalQuestionCategory,
} from "./medical-question";

// TES-1110 (Emelie 2026-10-05, Ö-331): "Ella Support is not an emergency
// channel. Medical questions that arrive there must be redirectable or
// escalatable internally to the care team."
//
// "Eskalera till vårdteamet" in the admin's Support inbox: staff hand a
// patient's support thread, or one message in it, to the doctor or the coach.
// It is an ordinary Handoff (TES-292) from the `admin` role, so it lands in
// the existing handoff queue and lane. The handoff carries a structured
// category and an optional short note; the patient's own words stay in the
// thread and are linked by message id, never copied.

// The internal category list. Staff-facing only: the patient never sees it.
// `other` requires a note, the same rule as DOCTOR_HANDOVER_REASONS.
// TES-1120 — the list moved to medical-question.ts, where the coach's
// "Skicka till läkare" uses it too; these names stay for TES-1110's callers.
export const SUPPORT_ESCALATION_CATEGORIES = MEDICAL_QUESTION_CATEGORIES;
export const supportEscalationCategorySchema = medicalQuestionCategorySchema;
export type SupportEscalationCategory = MedicalQuestionCategory;
export const SUPPORT_ESCALATION_CATEGORY_LABELS =
  MEDICAL_QUESTION_CATEGORY_LABELS;

/** The handoff's `reason` line, as the doctor or coach reads it in the queue. */
export function supportEscalationReason(
  category: SupportEscalationCategory,
): string {
  return `Fråga i Ella Support: ${SUPPORT_ESCALATION_CATEGORY_LABELS[category]}`;
}

/** A short note: context for the care team, not a copy of the message. */
export const SUPPORT_ESCALATION_NOTE_MAX = 500;

export const supportEscalationRequestSchema = z
  .object({
    toRole: handoffToRoleSchema,
    category: supportEscalationCategorySchema,
    note: z.string().trim().max(SUPPORT_ESCALATION_NOTE_MAX).optional(),
    // The patient message being escalated; absent = the whole thread.
    messageId: z.string().uuid().optional(),
    // Also post the standard reply (Ö-333) to the patient as Ella Support.
    // TES-1120 — only on an escalation to the doctor: the reply promises a
    // medical notice, which only the doctor publishes.
    sendRedirectReply: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.category === "other" && !v.note) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["note"],
        message: "note_required_for_other",
      });
    }
  });
export type SupportEscalationRequest = z.input<
  typeof supportEscalationRequestSchema
>;
export type SupportEscalationRequestParsed = z.output<
  typeof supportEscalationRequestSchema
>;

export const supportEscalationResponseSchema = z.object({
  handoff: handoffDtoSchema,
  // True when an open escalation of the same thread or message to the same
  // role already existed; it is returned instead and nothing new is sent.
  alreadyEscalated: z.boolean(),
  // The redirect reply posted to the patient, when one was asked for.
  redirectReply: z.object({ id: z.string(), createdAt: z.string() }).nullable(),
});
export type SupportEscalationResponse = z.infer<
  typeof supportEscalationResponseSchema
>;

// What the escalate form needs from the server. `redirectReply` is the text
// that would be posted, or null while there is none: the form hides the
// option entirely then.
export const supportEscalationOptionsSchema = z.object({
  redirectReply: z.string().nullable(),
});
export type SupportEscalationOptions = z.infer<
  typeof supportEscalationOptionsSchema
>;

// What the doctor or coach reads behind an escalation: the support thread
// (oldest first), with the escalated message named when there is one.
export const supportEscalationThreadSchema = z.object({
  handoffId: z.string(),
  linkedMessageId: z.string().nullable(),
  messages: z.array(chatMessageSchema),
});
export type SupportEscalationThread = z.infer<
  typeof supportEscalationThreadSchema
>;
